import * as React from "react";

import {
  ISingleLinkProps,
  ISingleLinkState,
  IInfoColumn,
  ISingleLinkParameter,
} from "../types";

import {
  Button,
  Icons,
  CopyButton,
  QR,
  TargetCheck,
  CreateShortlink,
  Tooltip,
} from "src/components";
import { SecondaryButton } from "./SecondaryButton";
import { LightPrimaryBg } from "./LightPrimaryBg";
import { TableCell } from "./TableCell";

enum ParameterGroup {
  General = "GeneralInformation",
  Channel = "ChannelSpecificInformation",
  Link = "LinkSpecificInformation",
  Ad = "AdSpecificInformation",
}

class SingleLink extends React.PureComponent<
  ISingleLinkProps,
  ISingleLinkState
> {
  parametersByGroup = this.getParametersByGroup(this.props.link.parameters);

  state: ISingleLinkState = {
    open: false,
    generatedShortlinks: [],
  };

  displayName = this.props.displayNameParameters
    .reduce((matches, parameterName) => {
      const match = this.props.link.parameters.find(
        (parameter) => parameter.name === parameterName
      );
      return match ? [...matches, match.value] : matches;
    }, [])
    .join(", ");

  groupColumns: IInfoColumn[] = [
    {
      name: "General information:",
      group: ParameterGroup.General,
    },
    {
      name: "Channel specific information:",
      group: ParameterGroup.Channel,
    },
    {
      name: "Link specific information:",
      group: ParameterGroup.Link,
    },
    {
      name: "Ad specific information:",
      group: ParameterGroup.Ad,
    },
  ];

  getParametersByGroup(parameters: ISingleLinkParameter[]) {
    const groups = {
      [ParameterGroup.General]: [],
      [ParameterGroup.Channel]: [],
      [ParameterGroup.Link]: [],
      [ParameterGroup.Ad]: [],
    };
    parameters.forEach((parameter) => {
      groups[parameter.group].push(parameter);
    });
    return groups;
  }
  getParametersByName() {
    const { parameterNames, link } = this.props;
    return parameterNames.map(({ name, length }) => {
      const possibleParameter = link.parameters.find((p) => p.name === name);
      if (!possibleParameter) {
        return { value: "", length };
      }
      return { value: possibleParameter.value, length };
    });
  }

  toggleOpen = () => {
    this.setState((state) => ({ open: !state.open }));
  };

  renderGroup = (column: IInfoColumn) => {
    const { open } = this.state;
    const parameters: ISingleLinkParameter[] =
      this.parametersByGroup[column.group];
    const visibleParameters = open ? parameters : parameters.slice(0, 4);
    return (
      <div className="flex-1 border border-grey" key={column.name}>
        <LightPrimaryBg className="p-3">{column.name}</LightPrimaryBg>
        <div>
          {visibleParameters.map((parameter, index) => (
            <div
              key={index}
              className="px-3 py-2 border-b border-grey word-break"
            >
              <span className="opacity-75 italic">{parameter.name}</span>:{" "}
              <p className="break-all">{parameter.value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  renderOpen = (viewType = "list") => {
    const { link, shortenerEnabled, targetUrlParameterName } = this.props;
    const { generatedShortlinks } = this.state;
    const targetURL = link.parameters.find(
      (el) => el.name === targetUrlParameterName
    );

    return (
      <div
        className={`${
          viewType === "list" ? "text-center" : ""
        } leading-normal mt-6 mb-4`}
      >
        {link.urls.map((url) => {
          const generated = generatedShortlinks.find((el) => el.id === url.id);
          const shortlink = generated ? generated.url : url.shortUrl;
          const urlLabel = url.isMain
            ? "Campaign URL"
            : url.displayName || "Additional Information";
          return (
            <div className="mb-2" key={url.id}>
              {url.url && (
                <>
                  <strong>{urlLabel}:</strong>{" "}
                  <span className="break-all">{url.url}</span>
                  {url.canCopy && (
                    <Tooltip text={"Copy " + urlLabel}>
                      <CopyButton
                        className="ml-3"
                        toCopy={url.url}
                        successMessage={urlLabel + " copied"}
                      />
                    </Tooltip>
                  )}
                  {url.showQR && <QR className="ml-1" url={url.url} />}
                </>
              )}
              {shortenerEnabled && url.canShorten && (
                <div className="mt-2">
                  {shortlink ? (
                    <>
                      <strong>Short link:</strong> {shortlink}
                      <Tooltip text="Copy shortlink">
                        <CopyButton
                          className="ml-3"
                          toCopy={shortlink}
                          successMessage="Shortlink copied"
                        />
                      </Tooltip>
                      <QR className="ml-1" url={shortlink} />
                    </>
                  ) : (
                    <CreateShortlink
                      linkId={url.id}
                      onGenerated={this.shortlinkGenerated(url.id)}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}

        {targetURL && (
          <p className="select-none">
            <strong>{targetUrlParameterName}:</strong>{" "}
            <span className="break-all">{targetURL.value}</span>
          </p>
        )}

        {link.creatorEmail && (
          <div
            className={`${
              viewType === "list" ? "justify-center" : ""
            } flex mt-4`}
          >
            Link created by {link.creatorEmail}
          </div>
        )}

        <div
          className={`${viewType === "list" ? "justify-center" : ""} flex mt-4`}
        >
          <TargetCheck id={link.id} />
          <Button
            size="small"
            background="grey"
            className="ml-4"
            onClick={this.props.duplicateLink}
          >
            Duplicate link
          </Button>
        </div>
      </div>
    );
  };

  shortlinkGenerated = (id: number) => (url: string) => {
    this.setState((state) => ({
      generatedShortlinks: [
        ...state.generatedShortlinks,
        {
          id,
          url,
        },
      ],
    }));
  };

  render() {
    const { link, isLoading, toggleArchived, isArchived, viewType, IDWidth } =
      this.props;
    const { open } = this.state;

    switch (viewType) {
      case "list":
        return (
          <div
            className={`bg-grey px-4 rounded mb-10 ${
              isLoading ? "loader" : ""
            }`}
          >
            <div className="flex items-center justify-between text-center py-3">
              <p>{link.created}</p>
              {this.displayName && (
                <p className="flex-1 text-xl">{this.displayName}</p>
              )}
              <div>
                <code className="align-middle font-mono text-base mr-4 inline-block w-32 text-overflow-ellipsis">
                  {link.visibleId}
                </code>
                <Tooltip text="Copy ID">
                  <CopyButton
                    toCopy={link.visibleId}
                    successMessage="Link ID copied"
                  />
                </Tooltip>
                <Tooltip
                  className="ml-6"
                  text={isArchived ? "Restore link" : "Archive link"}
                  side="right"
                >
                  <SecondaryButton onClick={toggleArchived}>
                    {isArchived ? (
                      <Icons.Circle character="↺" color="#474F54" />
                    ) : (
                      <Icons.Circle character="×" color="#474F54" />
                    )}
                  </SecondaryButton>
                </Tooltip>
              </div>
            </div>
            <div className="flex bg-grey-light mb-1">
              {this.groupColumns.map(this.renderGroup)}
            </div>

            {open && this.renderOpen()}
            <button
              className={`${
                open ? "sticky pin-l pin-b" : ""
              } block w-full py-2 leading-none text-black text-center bg-grey focus:outline-none hover:bg-grey-light`}
              onClick={this.toggleOpen}
            >
              <Icons.CaretDown className={open ? "flip-y" : ""} />
            </button>
          </div>
        );

      case "table":
        const parametersByName = this.getParametersByName();
        return (
          <div
            className={`border-b border-grey-light relative hover:z-10 ${
              isLoading ? "loader" : ""
            } ${open ? "bg-grey-light" : "bg-grey"}`}
          >
            <button
              onClick={this.toggleOpen}
              className="flex w-full text-black text-left hover:bg-grey-light"
            >
              <TableCell width={10}>{link.created}</TableCell>
              <TableCell width={IDWidth} className="font-mono">
                {link.visibleId}
              </TableCell>
              {parametersByName.map(({ value, length }, index) => (
                <TableCell key={`${value}-${index}`} width={length}>
                  {value}
                </TableCell>
              ))}
            </button>

            {open && (
              <div className="sticky z-10 pin-l max-w-lg border-t border-grey-dark p-4">
                <div className="flex items-center">
                  {this.displayName && (
                    <p className="flex-1 text-xl">{this.displayName}:</p>
                  )}
                  <div>
                    <code className="align-middle font-mono text-base mr-4 inline-block">
                      {link.visibleId}
                    </code>
                    <Tooltip text="Copy ID">
                      <CopyButton
                        toCopy={link.visibleId}
                        successMessage="Link ID copied"
                      />
                    </Tooltip>
                    <Tooltip
                      className="ml-6"
                      text={isArchived ? "Restore link" : "Archive link"}
                    >
                      <SecondaryButton onClick={toggleArchived}>
                        {isArchived ? (
                          <Icons.Circle character="↺" color="#474F54" />
                        ) : (
                          <Icons.Circle character="×" color="#474F54" />
                        )}
                      </SecondaryButton>
                    </Tooltip>
                  </div>
                </div>
                {this.renderOpen("table")}
              </div>
            )}
          </div>
        );
    }
  }
}

export default SingleLink;



// WEBPACK FOOTER //
// ./src/views/Links/components/SingleLink.tsx